import React, { useState, MutableRefObject } from 'react'

import { Dropdown } from '@sketch/components'
import { useModalContext } from 'containers/ModalManager/ModalContext'
import { ShareDropPayload } from 'modules/projects/components/MenuLink/useMenuShareDrop'
import { ConfirmMoveModal } from 'modules/projects/modals'

import { ProjectLink } from '../../types'
import { WorkspaceMinimalFragment } from '@sketch/gql-types'
import { DroppableLinkItem, ActionDropdown } from '../Item'

import { RenameInlineEditor } from '../InlineEditor'
import {
  CopyProjectAction,
  DeleteProjectAction,
  LeaveProjectAction,
  RenameProjectAction,
  ShareProjectAction,
} from '../ProjectDropdownActions'

import { useFlag } from 'containers/flags'

import { ReactComponent as FolderClosedIcon } from '@sketch/icons/folder-closed-16-svg'
import { ReactComponent as FolderOpenIcon } from '@sketch/icons/folder-open-16-svg'
import { ReactComponent as LockIcon } from '@sketch/icons/lock-16-svg'
import { ReactComponent as PeopleIcon } from '@sketch/icons/persons-two-16-svg'

export interface ProjectProps extends ProjectLink {
  userCanEdit: boolean
  userCanAdminister: boolean
  workspace: WorkspaceMinimalFragment
  refToLast?: React.MutableRefObject<HTMLElement>
  setEditedItem?: (value: string) => void
  autofocusLastCreated?: MutableRefObject<boolean>
  forceHighlight?: boolean
}

export const privacyIcon = (
  project: ProjectProps['project'],
  active: boolean = false
): React.ElementType => {
  switch (project.privacyIcon) {
    case 'GUESTS':
      return PeopleIcon

    case 'INVITED_WORKSPACE_MEMBERS':
      return LockIcon

    case 'WORKSPACE_MEMBERS':
      if (active) {
        return FolderOpenIcon
      } else {
        return FolderClosedIcon
      }
  }
}

export const ProjectListItem: React.FC<ProjectProps> = props => {
  const {
    link,
    project,
    userCanEdit,
    userCanAdminister,
    workspace,
    refToLast,
    setEditedItem,
    forceHighlight = false,
  } = props
  const { showModal } = useModalContext()
  const [isRenaming, setIsRenaming] = useState(false)
  const hasGuestsInProjects = useFlag('guests-in-project')

  const onDropped = ({ share }: ShareDropPayload) => {
    if (!share) {
      return
    }

    showModal(ConfirmMoveModal, {
      share,
      newProject: project,
      // Warn the user the document will become public
      isDraftDocument: share.project?.type === 'PERSONAL_DRAFTS',
    })
  }

  // only editors and admins can perform the following actions:
  //   - Add/remove people from the project (<ShareProjectAction .../>)
  //   - Rename the project (<RenameProjectAction .../>)
  //   - Delete the project (<DeleteProjectAction .../>)
  const showEditActions = userCanEdit || userCanAdminister
  // The `Leave...` button is being added as part of the guests-in-projects feature,
  // only users with the feature flag enabled AND who can leave the project
  // should see the button.
  const showLeaveAction = hasGuestsInProjects && project.userCanLeave
  const showDivider = showEditActions || showLeaveAction

  const workspaceId = workspace.identifier

  const dropdownContent = (
    <>
      <Dropdown.Header>Project</Dropdown.Header>
      {showEditActions && (
        <ShareProjectAction project={project} workspaceId={workspaceId} />
      )}
      {showEditActions && (
        <RenameProjectAction onRename={() => setIsRenaming(!isRenaming)} />
      )}
      <CopyProjectAction project={project} workspaceId={workspaceId} />
      {showDivider && <Dropdown.Divider />}
      {showLeaveAction && (
        <LeaveProjectAction
          projectIdentifier={project.identifier}
          projectName={project.name}
        />
      )}
      {showEditActions && (
        <DeleteProjectAction project={project} workspaceId={workspaceId} />
      )}
    </>
  )

  const toggleEditor = () => setIsRenaming(!isRenaming)
  const onCompleted = () => {
    setEditedItem?.(project.identifier)
  }

  if (isRenaming) {
    return (
      <RenameInlineEditor
        projectId={project.identifier}
        projectName={project.name}
        workspaceId={workspaceId}
        placeholder={project.name || 'Folder'}
        toggleEditor={toggleEditor}
        onCompleted={onCompleted}
      />
    )
  }

  return (
    <DroppableLinkItem
      icon={{
        icon: privacyIcon(project),
        activeIcon: privacyIcon(project, true),
      }}
      to={link}
      text={project.name}
      onDropped={onDropped}
      action={
        dropdownContent && (
          <ActionDropdown text="Rename & More…" content={dropdownContent} />
        )
      }
      ref={refToLast}
      aria-label={`Link to ${project.name}`}
      forceHighlight={forceHighlight}
    />
  )
}
